import React, { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { CalendarCheck, ChevronDown, Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { AppShell } from "src/components/layout/AppShell"
import { Button } from "src/components/ui/button"
import { Badge } from "src/components/ui/badge"
import { Input } from "src/components/ui/input"
import { Skeleton } from "src/components/ui/skeleton"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "src/components/ui/dropdown-menu"

import { useBookings } from "src/features/bookings/hooks/useBookings"
import {
  formatMoney, BOOKING_STATUS_BADGE, BOOKING_STATUS_LABEL,
} from "src/features/bookings/constants"
import { fetchProjects } from "src/features/projects/api/projectsApi"

const STATUSES = ["reserved", "confirmed", "completed", "cancelled"]

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

function PaymentBar({ received, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((received / total) * 100)) : 0
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-slate-400">{pct}%</span>
    </div>
  )
}

export default function BookingsPage() {
  const navigate = useNavigate()
  const { data, loading, fetch } = useBookings()
  const [status, setStatus] = useState("")
  const [projectId, setProjectId] = useState("")
  const [projects, setProjects] = useState([])
  const [searchRaw, setSearch] = useState("")
  const q = useDebounce(searchRaw)
  const [page, setPage] = useState(1)

  // Load projects for the filter dropdown
  useEffect(() => {
    fetchProjects({ per_page: 100 })
      .then((d) => setProjects(d.projects || []))
      .catch(() => {})
  }, [])

  const load = useCallback(
    () => fetch({ status, project_id: projectId, q, page, per_page: 20 }),
    [status, projectId, q, page, fetch]
  )
  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [status, projectId, q])

  const bookings = data?.bookings ?? []
  const meta = data?.meta

  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const sortedBookings = React.useMemo(() => {
    if (!sortField || !sortDirection) return bookings
    return [...bookings].sort((a, b) => {
      let aVal, bVal
      if (sortField === "customer") {
        aVal = a.customer?.name || ""
        bVal = b.customer?.name || ""
      } else if (sortField === "unit") {
        aVal = a.unit?.unit_number || ""
        bVal = b.unit?.unit_number || ""
      } else if (sortField === "project") {
        aVal = a.project?.name || ""
        bVal = b.project?.name || ""
      } else if (sortField === "status") {
        aVal = a.status || ""
        bVal = b.status || ""
      } else if (sortField === "value") {
        aVal = Number(a.total_value || 0)
        bVal = Number(b.total_value || 0)
      } else if (sortField === "collected") {
        aVal = Number(a.amount_received || 0)
        bVal = Number(b.amount_received || 0)
      } else if (sortField === "balance") {
        aVal = Number(a.balance_due || 0)
        bVal = Number(b.balance_due || 0)
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [bookings, sortField, sortDirection])

  const renderSortableHeader = (label, field, isRight = false) => {
    const isSorted = sortField === field
    return (
      <button
        onClick={() => handleSort(field)}
        className={`inline-flex items-center gap-1 hover:text-black focus:outline-none font-semibold text-black ${isRight ? "ml-auto justify-end" : ""}`}
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </button>
    )
  }

  return (
    <AppShell>
      <div className="px-8 py-7 space-y-5">
        {/* ── Header ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <CalendarCheck className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Bookings</h1>
              <p className="text-[13px] text-slate-500">
                {meta ? `${meta.total} booking${meta.total !== 1 ? "s" : ""}` : "Units sold from won deals"}
              </p>
            </div>
          </div>
        </div>

        {/* ── Filter bar (matches Deals layout) ── */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative max-w-xs flex-1">
            <Search style={{ left: "0.75rem" }} className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              style={{ paddingLeft: "2.5rem" }}
              placeholder="Search bookings…"
              value={searchRaw}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Status dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="justify-between gap-2 font-normal text-slate-700">
                {status ? BOOKING_STATUS_LABEL[status] : "All Statuses"}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-40">
              <DropdownMenuRadioGroup value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
                <DropdownMenuRadioItem value="all">All Statuses</DropdownMenuRadioItem>
                {STATUSES.map((s) => (
                  <DropdownMenuRadioItem key={s} value={s}>{BOOKING_STATUS_LABEL[s]}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Project dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="justify-between gap-2 font-normal text-slate-700">
                {projectId ? (projects.find((p) => String(p.id) === String(projectId))?.name || "Project") : "All Projects"}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuRadioGroup value={String(projectId || "all")} onValueChange={(v) => setProjectId(v === "all" ? "" : v)}>
                <DropdownMenuRadioItem value="all">All Projects</DropdownMenuRadioItem>
                {projects.map((p) => (
                  <DropdownMenuRadioItem key={p.id} value={String(p.id)}>{p.name}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* ── Table ── */}
        {loading && !data ? (
          <Skeleton className="h-64 w-full rounded-2xl" />
        ) : bookings.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400">
            No bookings yet. Win a deal and create a booking to get started.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">#</TableHead>
                  <TableHead>{renderSortableHeader("Customer", "customer")}</TableHead>
                  <TableHead>{renderSortableHeader("Unit", "unit")}</TableHead>
                  <TableHead>{renderSortableHeader("Project", "project")}</TableHead>
                  <TableHead>{renderSortableHeader("Status", "status")}</TableHead>
                  <TableHead className="text-right">
                    <div className="flex justify-end">
                      {renderSortableHeader("Value", "value", true)}
                    </div>
                  </TableHead>
                  <TableHead>
                    {renderSortableHeader("Collected", "collected")}
                  </TableHead>
                  <TableHead className="text-right">
                    <div className="flex justify-end">
                      {renderSortableHeader("Balance", "balance", true)}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedBookings.map((b, idx) => (
                  <TableRow key={b.id} className="cursor-pointer" onClick={() => navigate(`/bookings/${b.id}`)}>
                    <TableCell className="text-xs text-slate-400">{(page - 1) * 20 + idx + 1}</TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-800">{b.customer?.name || "—"}</div>
                      <div className="text-[11px] text-slate-400">{b.customer?.phone || ""}</div>
                    </TableCell>
                    <TableCell className="text-slate-600">{b.unit?.unit_number || "—"}</TableCell>
                    <TableCell className="text-slate-600">{b.project?.name || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={BOOKING_STATUS_BADGE[b.status] || "secondary"}>
                        {BOOKING_STATUS_LABEL[b.status] || b.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium text-slate-700">{formatMoney(b.total_value)}</TableCell>
                    <TableCell><PaymentBar received={b.amount_received} total={b.total_value} /></TableCell>
                    <TableCell className="text-right text-slate-600">{formatMoney(b.balance_due)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {meta && meta.total_pages > 1 && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <span className="text-slate-500">Page {meta.page} of {meta.total_pages}</span>
            <Button variant="outline" size="sm" disabled={page >= meta.total_pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        )}
      </div>
    </AppShell>
  )
}
